import { useState, useEffect } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth, useTheme, API } from "@/App";
import axios from "axios";
import { 
  LayoutDashboard, 
  Smartphone, 
  Package, 
  Users, 
  ShoppingCart, 
  Receipt, 
  FileBarChart2,
  LogOut,
  Store,
  Shield,
  Settings,
  Menu,
  X,
  Headphones
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Sidebar = ({ lojaSlug, lojaNome }) => {
  const { logout, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { slug } = useParams();
  const currentSlug = lojaSlug || slug || user?.loja_slug;
  
  const [lojaInfo, setLojaInfo] = useState({ nome: lojaNome, logo_url: null });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (currentSlug) {
      fetchLojaInfo();
    }
  }, [currentSlug]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [window.location.pathname]);

  const fetchLojaInfo = async () => {
    try {
      const response = await axios.get(`${API}/loja/${currentSlug}/verify`);
      setLojaInfo({
        nome: response.data.nome,
        logo_url: response.data.logo_url
      });
    } catch (error) {
      console.error("Error fetching store info:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const basePath = `/${currentSlug}`;

  const menuItems = [
    { path: basePath, icon: LayoutDashboard, label: "Início", end: true },
    { path: `${basePath}/modelos`, icon: Smartphone, label: "Modelos" },
    { path: `${basePath}/produtos`, icon: Package, label: "Celulares" },
    { path: `${basePath}/acessorios`, icon: Headphones, label: "Acessórios" },
    { path: `${basePath}/clientes`, icon: Users, label: "Clientes" },
    { path: `${basePath}/ponto-venda`, icon: ShoppingCart, label: "Ponto de Venda" },
    { path: `${basePath}/vendas`, icon: Receipt, label: "Vendas" },
    { path: `${basePath}/relatorio`, icon: FileBarChart2, label: "Relatório" },
    { path: `${basePath}/garantias`, icon: Shield, label: "Garantias" },
    { path: `${basePath}/configuracoes`, icon: Settings, label: "Configurações" },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <header className={`md:hidden fixed top-0 left-0 right-0 h-16 flex items-center justify-between px-4 z-50 transition-colors duration-300 ${
        isDark ? 'bg-[#0F0F0F] border-b border-white/5' : 'bg-white border-b border-gray-200 shadow-sm'
      }`}>
        <div className="flex items-center gap-3">
          {lojaInfo.logo_url ? (
            <img 
              src={lojaInfo.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${lojaInfo.logo_url}` : lojaInfo.logo_url} 
              alt={lojaInfo.nome || "Logo"} 
              className="w-8 h-8 rounded-lg object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-8 h-8 rounded-lg bg-[#D4AF37] items-center justify-center ${lojaInfo.logo_url ? 'hidden' : 'flex'}`}
          >
            <Store className="w-5 h-5 text-black" />
          </div>
          <span className={`font-semibold font-['Outfit'] truncate max-w-[150px] ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {lojaInfo.nome || lojaNome || "CellControl"}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={isDark ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile toggleable */}
      <aside 
        className={`
          fixed left-0 top-0 h-screen w-64 flex flex-col z-50 transition-all duration-300 ease-in-out
          md:translate-x-0
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:top-0 top-16 md:h-screen h-[calc(100vh-4rem)]
          ${isDark ? 'bg-[#0F0F0F] border-r border-white/5' : 'bg-white border-r border-gray-200 shadow-sm'}
        `}
        data-testid="sidebar"
      >
        {/* Logo - Only visible on desktop */}
        <div className={`hidden md:block p-6 ${isDark ? 'border-b border-white/5' : 'border-b border-gray-200'}`}>
          <div className="flex items-center gap-3">
            {lojaInfo.logo_url ? (
              <img 
                src={lojaInfo.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${lojaInfo.logo_url}` : lojaInfo.logo_url} 
                alt={lojaInfo.nome || "Logo"} 
                className="w-10 h-10 rounded-lg object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`w-10 h-10 rounded-lg bg-[#D4AF37] items-center justify-center ${lojaInfo.logo_url ? 'hidden' : 'flex'}`}
            >
              <Store className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className={`text-lg font-bold font-['Outfit'] ${isDark ? 'text-white' : 'text-gray-900'}`}>{lojaInfo.nome || lojaNome || "CellControl"}</h1>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Controle de Celulares</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={handleNavClick}
              data-testid={`sidebar-link-${item.path.split('/').pop() || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? isDark 
                      ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-[3px] border-[#D4AF37]"
                      : "bg-amber-50 text-amber-700 border-l-[3px] border-amber-600"
                    : isDark
                      ? "text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5"
                      : "text-gray-600 hover:text-amber-700 hover:bg-amber-50"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User & Logout */}
        <div className={`p-4 ${isDark ? 'border-t border-white/5' : 'border-t border-gray-200'}`}>
          {user && (
            <div className="mb-3 px-4 py-2">
              <p className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Logado como</p>
              <p className={`text-sm truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{user.user_email}</p>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={`w-full justify-start gap-3 ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-red-400/10' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair</span>
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
